import { useCallback, useEffect, useState, useRef, FormEvent } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Send,
  Clock,
  User,
  MessageSquare,
  Loader2,
  Mic,
  FileAudio,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { Ticket, TicketMessage } from '../types'
import {
  formatDate,
  getTicketStatusLabel,
  getTicketStatusColor,
  sanitizeHtml,
} from '../utils/helpers'

export default function TicketDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [messages, setMessages] = useState<TicketMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [transcribing, setTranscribing] = useState(false)
  const audioInputRef = useRef<HTMLInputElement>(null)

  const loadTicket = useCallback(async () => {
    if (!id) return
    try {
      const data = await api.getTicket(id)
      setTicket(data)
      setMessages(data.messages || [])
      setError(null)
    } catch (err) {
      console.error('Erreur chargement ticket:', err)
      setError(t('ticketDetail.loadError'))
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    loadTicket()
  }, [loadTicket])

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault()
    if (!id || (!newMessage.trim() && !audioFile)) return

    setSending(true)
    try {
      // Envoyer le message texte s'il y en a un
      if (newMessage.trim()) {
        await api.addTicketMessage(id, newMessage.trim())
      }
      // Envoyer l'audio s'il y en a un
      if (audioFile) {
        setTranscribing(true)
        await api.uploadTicketAudio(id, audioFile)
      }
      setNewMessage('')
      setAudioFile(null)
      if (audioInputRef.current) audioInputRef.current.value = ''
      await loadTicket()
    } catch (err) {
      console.error('Erreur envoi message:', err)
      alert(t('ticketDetail.sendError'))
    } finally {
      setSending(false)
      setTranscribing(false)
    }
  }

  const handleAudioFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 25 * 1024 * 1024) {
      alert(t('tickets.audioSizeError'))
      return
    }
    setAudioFile(file)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error || !ticket) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/tickets"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('ticketDetail.backToList')}
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800">{error || t('ticketDetail.notFound')}</p>
        </div>
      </div>
    )
  }

  const isClosed = ticket.status === 9 || ticket.status === 'closed'

  return (
    <div className="space-y-6 animate-fade-in">
      <Link
        to="/tickets"
        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('ticketDetail.backToList')}
      </Link>

      {/* En-tête du ticket */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{ticket.ref}</h1>
              <span
                className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getTicketStatusColor(ticket.status)}`}
              >
                {getTicketStatusLabel(ticket.status, t)}
              </span>
            </div>
            <h2 className="text-lg text-gray-700 mb-4">{ticket.subject}</h2>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">{t('ticketDetail.trackId')}</p>
                <p className="font-medium text-gray-900">{ticket.track_id}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('ticketDetail.createdOn')}</p>
                <p className="font-medium text-gray-900">{formatDate(ticket.date_creation)}</p>
              </div>
              {ticket.type && (
                <div>
                  <p className="text-gray-500">{t('ticketDetail.type')}</p>
                  <p className="font-medium text-gray-900">{ticket.type}</p>
                </div>
              )}
              {ticket.category && (
                <div>
                  <p className="text-gray-500">{t('ticketDetail.category')}</p>
                  <p className="font-medium text-gray-900">{ticket.category}</p>
                </div>
              )}
              {ticket.severity && (
                <div>
                  <p className="text-gray-500">{t('ticketDetail.priority')}</p>
                  <p className="font-medium text-gray-900">{ticket.severity}</p>
                </div>
              )}
              {ticket.progress !== undefined && (
                <div>
                  <p className="text-gray-500">{t('ticketDetail.progress')}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{ width: `${ticket.progress}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-gray-700">{ticket.progress}%</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Message initial */}
        {ticket.message && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-500 mb-2">
              {t('ticketDetail.description')}
            </p>
            <div
              className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(ticket.message) }}
            />
          </div>
        )}
      </div>

      {/* Messages / Conversation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary-600" />
            {t('ticketDetail.conversation')}
            {messages.length > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({messages.length} message{messages.length > 1 ? 's' : ''})
              </span>
            )}
          </h3>
        </div>

        <div className="divide-y divide-gray-50">
          {messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>{t('ticketDetail.noMessages')}</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {msg.fk_user_create
                        ? t('ticketDetail.user', { id: msg.fk_user_create })
                        : t('ticketDetail.support')}
                    </span>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(msg.date_creation)}
                    </div>
                  </div>
                  {msg.private === 1 && (
                    <span className="ml-auto text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                      {t('ticketDetail.private')}
                    </span>
                  )}
                </div>
                <div
                  className="ml-10 text-sm text-gray-700 whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.message) }}
                />
              </div>
            ))
          )}
        </div>

        {/* Formulaire de réponse */}
        {!isClosed && (
          <div className="p-6 border-t border-gray-100 bg-gray-50 space-y-3">
            {/* Fichier audio sélectionné */}
            {audioFile && (
              <div className="flex items-center gap-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                <FileAudio className="w-5 h-5 text-primary-600 flex-shrink-0" />
                <span className="text-sm text-primary-800 truncate flex-1">{audioFile.name}</span>
                <span className="text-xs text-primary-500">
                  {(audioFile.size / 1024 / 1024).toFixed(1)} Mo
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setAudioFile(null)
                    if (audioInputRef.current) audioInputRef.current.value = ''
                  }}
                  className="p-1 hover:bg-primary-100 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-primary-600" />
                </button>
              </div>
            )}

            <input
              ref={audioInputRef}
              type="file"
              accept="audio/mp3,audio/wav,audio/webm,audio/m4a,audio/mp4,audio/mpeg,audio/mpga,.mp3,.wav,.webm,.m4a"
              onChange={handleAudioFileChange}
              className="hidden"
            />

            <form onSubmit={handleSendMessage} className="flex flex-col gap-3">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('ticketDetail.messagePlaceholder')}
                rows={2}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none resize-none"
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => audioInputRef.current?.click()}
                  title={t('ticketDetail.attachAudio')}
                  className="px-3 py-3 border border-gray-300 text-gray-500 rounded-lg hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  <Mic className="w-5 h-5" />
                </button>
                <button
                  type="submit"
                  disabled={sending || (!newMessage.trim() && !audioFile)}
                  className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {transcribing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('ticketDetail.transcribing')}
                    </>
                  ) : sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {!transcribing && !sending && t('ticketDetail.send')}
                </button>
              </div>
            </form>
          </div>
        )}

        {isClosed && (
          <div className="p-6 border-t border-gray-100 bg-gray-50 text-center">
            <p className="text-sm text-gray-500">{t('ticketDetail.closed')}</p>
          </div>
        )}
      </div>
    </div>
  )
}
